import { trackEvent } from "@/lib/audit";

export type CouponDiscountType =
  | "percentage"
  | "fixed_amount"
  | "free_product"
  | "free_shipping"
  | "sweepstakes"
  | "cashback";

export interface SponsorCoupon {
  coupon_id: string;
  sponsor_id: string;
  sponsor_name: string;
  title: string;
  description: string;
  code: string;
  destination_url: string;
  discount_type: CouponDiscountType;
  discount_value: number; // % or amount
  starts_at: number;
  expires_at: number;
  max_redemptions: number;
  city_scope: string[];
  country_scope: string[];
  match_scope: string[];
  user_segments: string[];
  inventory_remaining: number;
  created_at: number;
  // analytics counters
  click_count: number;
  save_count: number;
  redemption_count: number;
}

export interface WalletEntry {
  entry_id: string;
  coupon_id: string;
  session_id: string;
  saved_at: number;
  state: "saved" | "redeemed" | "expired";
  reminded_at: number | null;
  redeemed_at: number | null;
}

const COUPONS_KEY = "ss2026_pr25_coupons";
const WALLET_KEY = "ss2026_pr25_wallet";

function load<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function save<T>(key: string, value: T) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* noop */
  }
}

export function listCoupons(): SponsorCoupon[] {
  return load<SponsorCoupon[]>(COUPONS_KEY, []);
}
export function listWallet(): WalletEntry[] {
  return load<WalletEntry[]>(WALLET_KEY, []);
}

export function createCoupon(input: Partial<SponsorCoupon> & { sponsor_name: string; title: string }): SponsorCoupon {
  const now = Date.now();
  const coupon: SponsorCoupon = {
    coupon_id: `cpn-${now}-${Math.random().toString(36).slice(2, 6)}`,
    sponsor_id: input.sponsor_id ?? `sp-${input.sponsor_name.toLowerCase()}`,
    sponsor_name: input.sponsor_name,
    title: input.title,
    description: input.description ?? "",
    code: input.code ?? `WC26-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    destination_url: input.destination_url ?? "#",
    discount_type: input.discount_type ?? "percentage",
    discount_value: input.discount_value ?? 10,
    starts_at: input.starts_at ?? now,
    expires_at: input.expires_at ?? now + 7 * 24 * 60 * 60_000,
    max_redemptions: input.max_redemptions ?? 1000,
    city_scope: input.city_scope ?? [],
    country_scope: input.country_scope ?? [],
    match_scope: input.match_scope ?? [],
    user_segments: input.user_segments ?? [],
    inventory_remaining: input.inventory_remaining ?? input.max_redemptions ?? 1000,
    created_at: now,
    click_count: 0,
    save_count: 0,
    redemption_count: 0,
  };
  const all = listCoupons();
  all.push(coupon);
  save(COUPONS_KEY, all);
  void trackEvent("coupon_created", { coupon_id: coupon.coupon_id, sponsor: coupon.sponsor_name });
  return coupon;
}

function bumpCoupon(coupon_id: string, field: "click_count" | "save_count" | "redemption_count") {
  const all = listCoupons();
  const c = all.find((x) => x.coupon_id === coupon_id);
  if (!c) return;
  c[field] += 1;
  if (field === "redemption_count") c.inventory_remaining = Math.max(0, c.inventory_remaining - 1);
  save(COUPONS_KEY, all);
}

export function clickCoupon(coupon_id: string) {
  bumpCoupon(coupon_id, "click_count");
  void trackEvent("coupon_clicked", { coupon_id });
}

export function saveCoupon(coupon_id: string, session_id: string): WalletEntry {
  const wallet = listWallet();
  const exists = wallet.find((w) => w.coupon_id === coupon_id && w.session_id === session_id && w.state === "saved");
  if (exists) return exists;
  const entry: WalletEntry = {
    entry_id: `w-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    coupon_id,
    session_id,
    saved_at: Date.now(),
    state: "saved",
    reminded_at: null,
    redeemed_at: null,
  };
  wallet.push(entry);
  save(WALLET_KEY, wallet);
  bumpCoupon(coupon_id, "save_count");
  void trackEvent("coupon_saved", { coupon_id, session_id });
  return entry;
}

export function redeemCoupon(entry_id: string) {
  const wallet = listWallet();
  const e = wallet.find((w) => w.entry_id === entry_id);
  if (!e || e.state !== "saved") return;
  e.state = "redeemed";
  e.redeemed_at = Date.now();
  save(WALLET_KEY, wallet);
  bumpCoupon(e.coupon_id, "redemption_count");
  void trackEvent("coupon_redeemed", { coupon_id: e.coupon_id, entry_id });
}

/**
 * Send reminders for entries expiring within 24h, mark expired entries.
 */
export function tickCouponWallet(now = Date.now()) {
  const wallet = listWallet();
  const coupons = listCoupons();
  let changed = false;
  for (const e of wallet) {
    const c = coupons.find((x) => x.coupon_id === e.coupon_id);
    if (!c) continue;
    if (e.state === "saved" && c.expires_at < now) {
      e.state = "expired";
      changed = true;
      void trackEvent("coupon_expired", { coupon_id: c.coupon_id, entry_id: e.entry_id });
    } else if (e.state === "saved" && !e.reminded_at && c.expires_at - now < 24 * 60 * 60_000) {
      e.reminded_at = now;
      changed = true;
      void trackEvent("coupon_reminder_sent", { coupon_id: c.coupon_id, entry_id: e.entry_id });
    }
  }
  if (changed) save(WALLET_KEY, wallet);
}

export function couponRoiByCity(): Array<{ city: string; clicks: number; saves: number; redemptions: number; roi: number }> {
  // Demo: derive by sponsor since we don't track per-city events; provide flat aggregate.
  const coupons = listCoupons();
  const total = coupons.reduce(
    (acc, c) => {
      acc.clicks += c.click_count;
      acc.saves += c.save_count;
      acc.redemptions += c.redemption_count;
      return acc;
    },
    { clicks: 0, saves: 0, redemptions: 0 },
  );
  return [{ city: "All cities", ...total, roi: total.clicks > 0 ? total.redemptions / total.clicks : 0 }];
}

export function seedDemoCoupons() {
  if (listCoupons().length > 0) return;
  createCoupon({
    sponsor_name: "Coca-Cola",
    title: "20% off 12-pack",
    description: "Celebrate every goal with Coca-Cola. 20% off any 12-pack.",
    discount_type: "percentage",
    discount_value: 20,
    country_scope: ["MX", "US"],
  });
  createCoupon({
    sponsor_name: "Nike",
    title: "Free shipping World Cup kits",
    description: "Free shipping on all national team kits during the Group Stage.",
    discount_type: "free_shipping",
    discount_value: 0,
    country_scope: ["MX", "US", "AR"],
    expires_at: Date.now() + 3 * 24 * 60 * 60_000,
  });
}